import { API_BASE } from '../../../hooks/admin/constants'
import { fetchWithPolicy } from '../fetchPolicy'

/**
 * Parses a JSON response body into a record while tolerating invalid payloads.
 */
export const parseResponseBody = async (response: Response): Promise<Record<string, unknown>> => {
  const raw = await response.text()
  if (!raw) return {}
  try {
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    return {}
  }
}

/**
 * Resolves user-facing error message from backend payload with fallback.
 */
export const toErrorMessage = (payload: Record<string, unknown>, fallback: string): string => {
  const message = payload.message
  if (typeof message === 'string' && message.trim()) {
    return message.trim()
  }
  return fallback
}

/**
 * Builds bearer authorization headers for admin API requests.
 */
export const toAuthHeaders = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
})

/**
 * Normalizes HeadersInit into plain string map.
 */
export const toHeaderRecord = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {}
  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries())
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers)
  }
  return { ...headers }
}

const mergeHeaders = (left: HeadersInit | undefined, right: HeadersInit | undefined): HeadersInit => ({
  ...toHeaderRecord(left),
  ...toHeaderRecord(right),
})

const isJsonContentType = (contentType: string | null): boolean =>
  typeof contentType === 'string' && contentType.includes('application/json')

export const resolveBearerTokenFromHeaders = (
  headers: HeadersInit | undefined | null,
): string | null => {
  if (!headers) return null
  const record = toHeaderRecord(headers)
  const authHeader = record.Authorization ?? record.authorization
  if (typeof authHeader !== 'string') return null
  const trimmed = authHeader.trim()
  const match = /^Bearer\s+(.+)$/i.exec(trimmed)
  return match?.[1]?.trim() || null
}

export type LegacyAuthHeaders = (withJson?: boolean) => Record<string, string>

/**
 * Extracts bearer token from legacy `authHeaders` callback format.
 *
 * @param authHeaders Legacy header builder.
 * @returns Bearer token string.
 * @throws Error when token is missing.
 */
export const resolveTokenFromLegacyAuthHeaders = (authHeaders: LegacyAuthHeaders): string => {
  const token = resolveBearerTokenFromHeaders(authHeaders(false))
  if (!token) {
    throw new Error('Admin token mancante')
  }
  return token
}

const toApiUrl = (path: string): string => {
  const trimmed = path.trim()
  if (!trimmed) return API_BASE
  if (/^https?:\/\//i.test(trimmed)) return trimmed

  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  if (normalized === API_BASE || normalized.startsWith(`${API_BASE}/`)) {
    return normalized
  }
  return `${API_BASE}${normalized}`
}

/**
 * Executes authenticated admin request and returns raw response + parsed payload.
 *
 * @param params Token, path and optional retry/timeout controls.
 * @returns Response object plus parsed payload record.
 */
export const requestAdmin = async ({
  token,
  path,
  init,
  timeoutMs,
  retries,
  retryDelayMs,
}: {
  token: string
  path: string
  init?: RequestInit
  timeoutMs?: number
  retries?: number
  retryDelayMs?: number
}): Promise<{ response: Response; payload: Record<string, unknown>; contentType: string | null; rawText: string }> => {
  const response = await fetchWithPolicy({
    request: toApiUrl(path),
    init: {
      ...init,
      headers: mergeHeaders(toAuthHeaders(token), init?.headers),
    },
    timeoutMs,
    retries,
    retryDelayMs,
  })
  const contentType = response.headers.get('content-type')
  const rawText = await response.text()
  const payload = rawText
    ? (() => {
        try {
          return JSON.parse(rawText) as Record<string, unknown>
        } catch {
          return {}
        }
      })()
    : {}
  return { response, payload, contentType, rawText }
}

/**
 * Executes authenticated admin request and enforces successful status code.
 *
 * @param params Token, path and optional fetch-policy overrides.
 * @returns Parsed JSON payload for successful responses.
 * @throws Error when response is not OK.
 */
export const requestAdminJson = async ({
  token,
  path,
  init,
  fallbackError,
  timeoutMs,
  retries,
  retryDelayMs,
}: {
  token: string
  path: string
  init?: RequestInit
  fallbackError: string
  timeoutMs?: number
  retries?: number
  retryDelayMs?: number
}): Promise<Record<string, unknown>> => {
  const { response, payload, contentType, rawText } = await requestAdmin({
    token,
    path,
    init,
    timeoutMs,
    retries,
    retryDelayMs,
  })
  const snippet = rawText.slice(0, 200).replace(/\s+/g, ' ').trim()
  if (!isJsonContentType(contentType)) {
    console.error('[admin-backoffice] Unexpected non-JSON response', {
      path,
      status: response.status,
      contentType,
      snippet,
    })
    throw new BackofficeApiError({
      message: `Unexpected non-JSON response from ${path}`,
      status: response.status,
      path,
      payload,
    })
  }
  if (!response.ok) {
    throw new BackofficeApiError({
      message: toErrorMessage(payload, fallbackError),
      status: response.status,
      path,
      payload,
    })
  }
  return payload
}

export class BackofficeApiError extends Error {
  readonly status: number
  readonly path: string
  readonly payload: Record<string, unknown>
  readonly code: string

  constructor({
    message,
    status,
    path,
    payload = {},
  }: {
    message: string
    status: number
    path: string
    payload?: Record<string, unknown>
  }) {
    super(message)
    this.name = 'BackofficeApiError'
    this.status = status
    this.path = path
    this.payload = payload
    this.code = typeof payload.code === 'string' ? payload.code : ''
  }
}
